import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/api";
import { EXPENSE_CATEGORIES } from "@/constants/modules";
import { BarChart3, Wallet, FileCheck, FileX, TrendingDown, Home } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function YearSummaryTab({ year }) {
  const [summary, setSummary] = useState(null);
  const [t777Total, setT777Total] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.allSettled([
        api.get("/expenses/stats/summary", { params: { tax_year: year } }),
        api.get("/t777", { params: { tax_year: year } }),
      ]);
      if (s.status === "fulfilled") setSummary(s.value.data);
      if (t.status === "fulfilled") {
        const total = (t.value.data || []).reduce((acc, r) => acc + (r.deductible_amount || 0), 0);
        setT777Total(total);
      }
    } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const chartData = useMemo(() => (summary?.monthly || []).map(m => ({
    month: (m.month || "").slice(5),
    Montant: m.amount,
  })), [summary]);

  const topCategories = useMemo(() => {
    const entries = Object.entries(summary?.by_category || {}).sort((a, b) => b[1] - a[1]).slice(0, 5);
    return entries.map(([cat, amt]) => ({
      label: EXPENSE_CATEGORIES.find(c => c.id === cat)?.label || cat,
      amount: amt,
    }));
  }, [summary]);

  if (loading) {
    return (
      <div className="px-4 py-10 text-center text-gray-400" data-testid="summary-loading">
        <div className="w-5 h-5 border-2 border-red-200 border-t-red-600 rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (!summary || !summary.count) {
    return (
      <div className="bg-white border rounded-xl px-4 py-10 text-center text-gray-400" data-testid="summary-empty">
        <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">Aucune depense enregistree pour {year}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="summary-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard icon={Wallet} color="red" label="Total depenses" value={`${summary.total.toFixed(2)} $`} sub={`${summary.count} entree(s)`} />
        <SummaryCard icon={TrendingDown} color="green" label="Deductibles" value={`${summary.deductible.toFixed(2)} $`} sub={`Auto : ${summary.autonome.toFixed(2)} $`} />
        <SummaryCard icon={Wallet} color="gray" label="Non deductibles" value={`${summary.non_deductible.toFixed(2)} $`} sub={`Interne : ${summary.interne.toFixed(2)} $`} />
        <SummaryCard icon={Home} color="indigo" label="T777 cumule" value={`${t777Total.toFixed(2)} $`} sub="Bureau domicile" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard icon={FileCheck} color="green" label="Avec justificatif" value={summary.with_receipt} />
        <SummaryCard icon={FileX} color="orange" label="Sans justificatif" value={summary.without_receipt} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border rounded-xl p-4" data-testid="summary-monthly-chart">
          <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><TrendingDown className="w-4 h-4 text-red-500" /> Depenses par mois</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={60} />
                <Tooltip formatter={(v) => `${v.toFixed(2)} $`} />
                <Bar dataKey="Montant" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[260px] flex items-center justify-center text-gray-400 text-sm">Aucune donnee mensuelle</div>
          )}
        </div>

        <div className="bg-white border rounded-xl overflow-hidden" data-testid="summary-top-categories">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h3 className="font-semibold text-sm">Top 5 categories</h3>
          </div>
          <div className="divide-y">
            {topCategories.map((c, i) => (
              <div key={c.label} className="px-4 py-2.5 flex items-center justify-between">
                <span className="text-sm text-gray-700"><span className="text-xs text-gray-400 mr-2">#{i + 1}</span>{c.label}</span>
                <span className="text-sm font-semibold text-red-600 tabular-nums">{c.amount.toFixed(2)} $</span>
              </div>
            ))}
            {!topCategories.length && <div className="px-4 py-6 text-center text-gray-400 text-sm">Aucune donnee</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, color, label, value, sub }) {
  const colors = {
    red: "bg-red-50 text-red-600",
    green: "bg-green-50 text-green-600",
    gray: "bg-gray-50 text-gray-600",
    indigo: "bg-indigo-50 text-indigo-600",
    orange: "bg-orange-50 text-orange-600",
  };
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${colors[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-lg font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
      {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}
